import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { roleLabel } from "@/lib/roles";

const ROLES = ["admin", "lead", "staff", "management", "kiosk", "maintenance"];

export default function AddUserForm({ onAdded }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("staff");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/users", { email, name, role });
      toast.success(data.invite_sent_at
        ? `${data.name} added as ${roleLabel(data.role)} — branded invite emailed to ${data.email}.`
        : `${data.name} added as ${roleLabel(data.role)}. ${data.invite_error ? "Invite email failed — use Resend invite." : "Invite email not sent (email not configured yet)."}`);
      setEmail(""); setName(""); setRole("staff");
      onAdded?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not add user");
    } finally {
      setBusy(false);
    }
  };

  const field = "h-10 rounded-sm border-[#cfcfc9] bg-white text-sm text-[#232826] focus-visible:ring-[#647265]";

  return (
    <div className="border border-[#e6e5df] bg-white rounded-sm" data-testid="add-user-card">
      <div className="px-5 py-4 border-b border-[#e6e5df] flex items-center gap-3">
        <UserPlus size={16} className="text-[#647265]" />
        <div className="font-display font-bold text-[#232826]">Add User</div>
        <div className="ml-auto text-[10px] uppercase tracking-widest text-[#a39f98] font-mono hidden sm:block">Microsoft account · invite-only</div>
      </div>
      <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-[1fr_1fr_160px_auto] gap-3 items-end" data-testid="add-user-form">
        <div className="space-y-1.5">
          <label className="text-[10px] uppercase tracking-widest text-[#6b6a68]" htmlFor="add-user-email">Work email (Microsoft)</label>
          <Input id="add-user-email" data-testid="add-user-email" type="email" required placeholder="name@misco.kiwi" value={email} onChange={(e) => setEmail(e.target.value)} className={field} />
        </div>
        <div className="space-y-1.5">
          <label className="text-[10px] uppercase tracking-widest text-[#6b6a68]" htmlFor="add-user-name">Name (optional)</label>
          <Input id="add-user-name" data-testid="add-user-name" placeholder="Filled from Microsoft on first sign-in" value={name} onChange={(e) => setName(e.target.value)} className={field} />
        </div>
        <div className="space-y-1.5">
          <label className="text-[10px] uppercase tracking-widest text-[#6b6a68]">Role</label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className={field} data-testid="add-user-role"><SelectValue /></SelectTrigger>
            <SelectContent className="border-[#e6e5df] bg-white">
              {ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={busy} data-testid="add-user-submit"
          className="h-10 rounded-sm bg-[#424e43] hover:bg-[#232826] text-[#f7f5f1] text-xs uppercase tracking-wide font-semibold px-5">
          {busy ? "Adding…" : "Add user"}
        </Button>
        <p className="md:col-span-4 text-xs text-[#6b6a68]">
          Only people added here can sign in. They receive a MISCO-branded invite email and get this role automatically on their first Microsoft sign-in.
        </p>
      </form>
    </div>
  );
}
